/**
 * Door Sensor Calculations Utility
 *
 * مكتبة شاملة لحساب جميع القيم المتعلقة بحساسات الأبواب:
 * - حساب عدد مرات الفتح/الإغلاق
 * - حساب مدد الفتح والإغلاق
 * - تحليل أنماط الاستخدام (24 ساعة)
 * - تحليل النشاط وساعات الذروة
 * - كشف الحالات غير الطبيعية (باب مفتوح طويلاً، كثرة الاستخدام)
 *
 * @version 2.4.0
 * @since January 26, 2026
 */

// ═══════════════════════════════════════════════════════════
// Type Definitions - تعريفات الأنواع
// ═══════════════════════════════════════════════════════════

/**
 * Door state enum
 */
export type DoorState = 'open' | 'closed' | 'ajar';

/**
 * Door event - single opening/closing event
 */
export interface DoorEvent {
  timestamp: Date; // Event time
  state: DoorState; // Door state after event
  duration?: number; // Duration in seconds (for open/ajar states)
  triggeredBy?: string; // User/person who triggered (optional)
  forced?: boolean; // Was door forced open? (security)
  alarmTriggered?: boolean; // Did event trigger alarm?
}

/**
 * Hourly door activity pattern
 */
export interface DoorHourPattern {
  hour: number; // Hour (0-23)
  openCount: number; // Times opened this hour
  closeCount: number; // Times closed this hour
  totalOpenSeconds: number; // Total seconds door was open
  averageOpenDuration: number; // Average open duration
  isPeakHour: boolean; // Is this a peak activity hour?
}

/**
 * Door timeline item for display
 */
export interface DoorTimelineItem {
  timestamp: Date;
  formattedTime: string; // "2:30 PM" or "14:30"
  state: DoorState;
  stateLabel: string; // "Opened" or "فُتح"
  duration?: number;
  formattedDuration?: string; // "2m 5s" or "٢ د ٥ ث"
  triggeredBy?: string;
  forced?: boolean;
  alarmTriggered?: boolean;
}

/**
 * Comprehensive door statistics
 */
export interface DoorStats {
  totalOpens: number; // Total times opened
  totalCloses: number; // Total times closed
  totalOpenSeconds: number; // Total seconds open
  totalClosedSeconds: number; // Total seconds closed
  averageOpenDuration: number; // Average open duration (seconds)
  longestOpen: number; // Longest open duration (seconds)
  shortestOpen: number; // Shortest open duration (seconds)
  peakHour: number; // Most active hour (0-23)
  quietestHour: number; // Least active hour (0-23)
  forcedOpenCount: number; // Times forced open
  alarmTriggeredCount: number; // Times alarm triggered
  abnormalOpenings: number; // Unusually long opens
  securityScore: number; // Security score (0-100)
}

/**
 * Door sensor status
 */
export interface DoorSensorStatus {
  currentState: DoorState; // Current door state
  stateLabel: string; // "Open" or "مفتوح"
  timeInCurrentState: number; // Minutes in current state
  isAbnormal: boolean; // Door open too long?
  openCountToday: number; // Opens today
  openCountWeek: number; // Opens this week
  openCountMonth: number; // Opens this month
  averageOpenDurationToday: number; // Average today (seconds)
  longestOpenToday: number; // Longest open today (seconds)
  forcedOpenToday: number; // Times forced today
  alarmTriggeredToday: number; // Alarms today
  lastOpened: Date | null; // Last open time
  lastClosed: Date | null; // Last close time
  batteryLevel?: number; // Battery % (optional)
  signalStrength?: number; // Signal strength (optional)
  tamperDetected?: boolean; // Tamper alert (optional)
}

// ═══════════════════════════════════════════════════════════
// Core Calculation Functions - دوال الحسابات الأساسية
// ═══════════════════════════════════════════════════════════

/**
 * Calculate number of door opens within date range
 *
 * @param events - Array of door events
 * @param startDate - Start of counting period
 * @param endDate - End of counting period
 * @returns Number of times door was opened
 *
 * @example
 * const opensToday = calculateOpenCount(events, todayStart, now);
 */
export function calculateOpenCount(events: DoorEvent[], startDate: Date, endDate: Date): number {
  if (!events || events.length === 0) return 0;

  return events.filter(
    (event) => event.state === 'open' && event.timestamp >= startDate && event.timestamp <= endDate,
  ).length;
}

/**
 * Calculate total duration door was open
 *
 * @param events - Array of door events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Total seconds door was open
 *
 * @example
 * const totalOpenSeconds = getOpenDuration(events, todayStart, now);
 */
export function getOpenDuration(events: DoorEvent[], startDate: Date, endDate: Date): number {
  if (!events || events.length === 0) return 0;

  // Filter events in range
  const rangeEvents = events.filter((e) => e.timestamp >= startDate && e.timestamp <= endDate);

  let totalSeconds = 0;

  // Sort by timestamp
  const sortedEvents = [...rangeEvents].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  // Calculate duration between open and close events
  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];

    if (event.state === 'open' || event.state === 'ajar') {
      // If duration is provided, use it
      if (event.duration) {
        totalSeconds += event.duration;
      } else {
        // Otherwise, find next close event
        const nextCloseIndex = sortedEvents.findIndex((e, idx) => idx > i && e.state === 'closed');

        if (nextCloseIndex !== -1) {
          const duration =
            (sortedEvents[nextCloseIndex].timestamp.getTime() - event.timestamp.getTime()) / 1000;
          totalSeconds += duration;
        }
      }
    }
  }

  return Math.floor(totalSeconds);
}

/**
 * Calculate average open duration
 *
 * @param events - Array of door events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Average open duration in seconds
 */
export function getAverageOpenDuration(
  events: DoorEvent[],
  startDate: Date,
  endDate: Date,
): number {
  const openCount = calculateOpenCount(events, startDate, endDate);
  if (openCount === 0) return 0;

  const totalDuration = getOpenDuration(events, startDate, endDate);
  return Math.floor(totalDuration / openCount);
}

/**
 * Get 24-hour door activity pattern
 *
 * @param events - Array of door events
 * @param date - Date to analyze (defaults to today)
 * @returns Array of 24 hourly patterns
 *
 * @example
 * const todayPattern = getDoorPattern(events);
 * const peakHours = todayPattern.filter(h => h.isPeakHour);
 */
export function getDoorPattern(events: DoorEvent[], date?: Date): DoorHourPattern[] {
  const targetDate = date || new Date();
  const dayStart = new Date(targetDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);

  // Initialize 24-hour array
  const hourlyData: DoorHourPattern[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    openCount: 0,
    closeCount: 0,
    totalOpenSeconds: 0,
    averageOpenDuration: 0,
    isPeakHour: false,
  }));

  if (!events || events.length === 0) return hourlyData;

  // Filter events for the target day
  const dayEvents = events.filter((e) => e.timestamp >= dayStart && e.timestamp <= dayEnd);

  // Count events per hour
  dayEvents.forEach((event) => {
    const hour = event.timestamp.getHours();

    if (event.state === 'open' || event.state === 'ajar') {
      hourlyData[hour].openCount++;
      if (event.duration) {
        hourlyData[hour].totalOpenSeconds += event.duration;
      }
    } else if (event.state === 'closed') {
      hourlyData[hour].closeCount++;
    }
  });

  // Calculate averages
  hourlyData.forEach((hour) => {
    if (hour.openCount > 0) {
      hour.averageOpenDuration = Math.floor(hour.totalOpenSeconds / hour.openCount);
    }
  });

  // Identify peak hours (above average + 1 std deviation)
  const openCounts = hourlyData.map((h) => h.openCount);
  const avgOpens = openCounts.reduce((sum, count) => sum + count, 0) / 24;
  const variance = openCounts.reduce((sum, count) => sum + Math.pow(count - avgOpens, 2), 0) / 24;
  const stdDev = Math.sqrt(variance);
  const peakThreshold = avgOpens + stdDev;

  hourlyData.forEach((hour) => {
    hour.isPeakHour = hour.openCount > peakThreshold;
  });

  return hourlyData;
}

/**
 * Get door activity timeline
 *
 * @param events - Array of door events
 * @param maxItems - Maximum items to return (default: 20)
 * @returns Array of timeline items (most recent first)
 *
 * @example
 * const recentActivity = getDoorTimeline(events, 10);
 */
export function getDoorTimeline(events: DoorEvent[], maxItems: number = 20): DoorTimelineItem[] {
  if (!events || events.length === 0) return [];

  // Sort by most recent first
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const timeline: DoorTimelineItem[] = [];

  sortedEvents.slice(0, maxItems).forEach((event) => {
    const item: DoorTimelineItem = {
      timestamp: event.timestamp,
      formattedTime: event.timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      state: event.state,
      stateLabel: event.state === 'open' ? 'Opened' : event.state === 'closed' ? 'Closed' : 'Ajar',
      triggeredBy: event.triggeredBy,
      forced: event.forced,
      alarmTriggered: event.alarmTriggered,
    };

    if (event.duration) {
      item.duration = event.duration;
      item.formattedDuration = formatOpenDuration(event.duration, 'en');
    }

    timeline.push(item);
  });

  return timeline;
}

/**
 * Calculate comprehensive door statistics
 *
 * @param events - Array of door events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Complete statistics object
 *
 * @example
 * const stats = getDoorStatistics(events, startOfWeek, endOfWeek);
 */
export function getDoorStatistics(events: DoorEvent[], startDate: Date, endDate: Date): DoorStats {
  if (!events || events.length === 0) {
    return {
      totalOpens: 0,
      totalCloses: 0,
      totalOpenSeconds: 0,
      totalClosedSeconds: 0,
      averageOpenDuration: 0,
      longestOpen: 0,
      shortestOpen: 0,
      peakHour: 0,
      quietestHour: 0,
      forcedOpenCount: 0,
      alarmTriggeredCount: 0,
      abnormalOpenings: 0,
      securityScore: 100,
    };
  }

  // Filter events in range
  const rangeEvents = events.filter((e) => e.timestamp >= startDate && e.timestamp <= endDate);

  // Count opens and closes
  const totalOpens = rangeEvents.filter((e) => e.state === 'open').length;
  const totalCloses = rangeEvents.filter((e) => e.state === 'closed').length;

  // Calculate total open duration
  const totalOpenSeconds = getOpenDuration(rangeEvents, startDate, endDate);

  // Calculate total closed duration
  const totalPeriodSeconds = (endDate.getTime() - startDate.getTime()) / 1000;
  const totalClosedSeconds = Math.max(0, totalPeriodSeconds - totalOpenSeconds);

  // Calculate average, longest, shortest open durations
  const openDurations: number[] = [];
  const sortedEvents = [...rangeEvents].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    if (event.state === 'open') {
      if (event.duration) {
        openDurations.push(event.duration);
      } else {
        const nextCloseIndex = sortedEvents.findIndex((e, idx) => idx > i && e.state === 'closed');
        if (nextCloseIndex !== -1) {
          const duration =
            (sortedEvents[nextCloseIndex].timestamp.getTime() - event.timestamp.getTime()) / 1000;
          openDurations.push(duration);
        }
      }
    }
  }

  const averageOpenDuration =
    openDurations.length > 0
      ? openDurations.reduce((sum, d) => sum + d, 0) / openDurations.length
      : 0;
  const longestOpen = openDurations.length > 0 ? Math.max(...openDurations) : 0;
  const shortestOpen = openDurations.length > 0 ? Math.min(...openDurations) : 0;

  // Find peak and quietest hours
  const pattern = getDoorPattern(rangeEvents);
  const peakHourData = pattern.reduce((prev, curr) =>
    curr.openCount > prev.openCount ? curr : prev,
  );
  const quietestHourData = pattern.reduce((prev, curr) =>
    curr.openCount < prev.openCount ? curr : prev,
  );

  // Count security events
  const forcedOpenCount = rangeEvents.filter((e) => e.forced).length;
  const alarmTriggeredCount = rangeEvents.filter((e) => e.alarmTriggered).length;

  // Count abnormal openings (>30 minutes)
  const abnormalOpenings = openDurations.filter((d) => d > 1800).length;

  // Calculate security score (0-100)
  let securityScore = 100;
  securityScore -= forcedOpenCount * 10; // -10 per forced open
  securityScore -= alarmTriggeredCount * 15; // -15 per alarm
  securityScore -= abnormalOpenings * 5; // -5 per abnormal open
  securityScore = Math.max(0, Math.min(100, securityScore));

  return {
    totalOpens,
    totalCloses,
    totalOpenSeconds: Math.floor(totalOpenSeconds),
    totalClosedSeconds: Math.floor(totalClosedSeconds),
    averageOpenDuration: Math.floor(averageOpenDuration),
    longestOpen: Math.floor(longestOpen),
    shortestOpen: Math.floor(shortestOpen),
    peakHour: peakHourData.hour,
    quietestHour: quietestHourData.hour,
    forcedOpenCount,
    alarmTriggeredCount,
    abnormalOpenings,
    securityScore,
  };
}

/**
 * Get current door sensor status
 *
 * @param events - Array of all door events
 * @param currentState - Current door state
 * @param batteryLevel - Current battery level (optional)
 * @param signalStrength - Signal strength (optional)
 * @param tamperDetected - Tamper detection flag (optional)
 * @returns Complete sensor status
 *
 * @example
 * const status = getDoorSensorStatus(events, 'closed', 85, 90, false);
 */
export function getDoorSensorStatus(
  events: DoorEvent[],
  currentState: DoorState = 'closed',
  batteryLevel?: number,
  signalStrength?: number,
  tamperDetected?: boolean,
): DoorSensorStatus {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setMonth(now.getMonth() - 1);

  // Find last open and close events
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const lastOpened = sortedEvents.find((e) => e.state === 'open')?.timestamp || null;
  const lastClosed = sortedEvents.find((e) => e.state === 'closed')?.timestamp || null;

  // Calculate time in current state
  let timeInCurrentState = 0;
  const lastEvent = sortedEvents[0];
  if (lastEvent && lastEvent.state === currentState) {
    timeInCurrentState = Math.floor((now.getTime() - lastEvent.timestamp.getTime()) / 60000);
  }

  // Check if abnormally open (>30 minutes)
  const isAbnormal = currentState === 'open' && timeInCurrentState > 30;

  // Count opens per period
  const openCountToday = calculateOpenCount(events, todayStart, now);
  const openCountWeek = calculateOpenCount(events, weekStart, now);
  const openCountMonth = calculateOpenCount(events, monthStart, now);

  // Calculate average open duration today
  const averageOpenDurationToday = getAverageOpenDuration(events, todayStart, now);

  // Find longest open today
  const todayEvents = events.filter((e) => e.timestamp >= todayStart && e.timestamp <= now);
  const todayStats = getDoorStatistics(todayEvents, todayStart, now);
  const longestOpenToday = todayStats.longestOpen;

  // Count security events today
  const forcedOpenToday = todayEvents.filter((e) => e.forced).length;
  const alarmTriggeredToday = todayEvents.filter((e) => e.alarmTriggered).length;

  // Get state label
  const stateLabel =
    currentState === 'open' ? 'Open' : currentState === 'closed' ? 'Closed' : 'Ajar';

  return {
    currentState,
    stateLabel,
    timeInCurrentState,
    isAbnormal,
    openCountToday,
    openCountWeek,
    openCountMonth,
    averageOpenDurationToday,
    longestOpenToday,
    forcedOpenToday,
    alarmTriggeredToday,
    lastOpened,
    lastClosed,
    batteryLevel,
    signalStrength,
    tamperDetected,
  };
}

// ═══════════════════════════════════════════════════════════
// Formatting Functions - دوال التنسيق
// ═══════════════════════════════════════════════════════════

/**
 * Format door state for display
 *
 * @param state - Door state
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted state string
 *
 * @example
 * formatDoorState('open', 'ar') // "مفتوح"
 * formatDoorState('closed', 'en') // "Closed"
 */
export function formatDoorState(state: DoorState, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    return state === 'open' ? 'مفتوح' : state === 'closed' ? 'مغلق' : 'نصف مفتوح';
  }

  return state === 'open' ? 'Open' : state === 'closed' ? 'Closed' : 'Ajar';
}

/**
 * Format open duration for display
 *
 * @param seconds - Duration in seconds
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted duration string
 *
 * @example
 * formatOpenDuration(125, 'en') // "2m 5s"
 * formatOpenDuration(125, 'ar') // "٢ د ٥ ث"
 */
export function formatOpenDuration(seconds: number, language: 'ar' | 'en' = 'en'): string {
  if (seconds < 60) {
    const formatted = language === 'ar' ? `${seconds.toLocaleString('ar-SA')} ث` : `${seconds}s`;
    return formatted;
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (language === 'ar') {
      return secs > 0
        ? `${minutes.toLocaleString('ar-SA')} د ${secs.toLocaleString('ar-SA')} ث`
        : `${minutes.toLocaleString('ar-SA')} د`;
    }

    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  }

  // Hours
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (language === 'ar') {
    return minutes > 0
      ? `${hours.toLocaleString('ar-SA')} س ${minutes.toLocaleString('ar-SA')} د`
      : `${hours.toLocaleString('ar-SA')} س`;
  }

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Format time since last event
 *
 * @param minutes - Time in minutes
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted time string
 *
 * @example
 * formatTimeSince(5, 'en') // "5 minutes ago"
 * formatTimeSince(5, 'ar') // "منذ ٥ دقائق"
 */
export function formatTimeSince(minutes: number, language: 'ar' | 'en' = 'en'): string {
  if (minutes < 1) {
    return language === 'ar' ? 'الآن' : 'Just now';
  }

  if (minutes < 60) {
    const formatted =
      language === 'ar'
        ? `منذ ${minutes.toLocaleString('ar-SA')} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`
        : `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    return formatted;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const formatted =
      language === 'ar'
        ? `منذ ${hours.toLocaleString('ar-SA')} ${hours === 1 ? 'ساعة' : 'ساعات'}`
        : `${hours} hour${hours === 1 ? '' : 's'} ago`;
    return formatted;
  }

  const days = Math.floor(hours / 24);
  const formatted =
    language === 'ar'
      ? `منذ ${days.toLocaleString('ar-SA')} ${days === 1 ? 'يوم' : 'أيام'}`
      : `${days} day${days === 1 ? '' : 's'} ago`;
  return formatted;
}

/**
 * Get activity level description
 *
 * @param openCount - Number of opens
 * @param threshold - Threshold for high activity (default: 20)
 * @param language - Display language ('ar' or 'en')
 * @returns Activity level description
 *
 * @example
 * getActivityLevel(35, 20, 'en') // "High Activity"
 * getActivityLevel(5, 20, 'ar') // "نشاط منخفض"
 */
export function getActivityLevel(
  openCount: number,
  threshold: number = 20,
  language: 'ar' | 'en' = 'en',
): string {
  if (openCount === 0) {
    return language === 'ar' ? 'لا يوجد نشاط' : 'No Activity';
  }

  if (openCount < threshold / 2) {
    return language === 'ar' ? 'نشاط منخفض' : 'Low Activity';
  }

  if (openCount < threshold) {
    return language === 'ar' ? 'نشاط متوسط' : 'Medium Activity';
  }

  return language === 'ar' ? 'نشاط عالي' : 'High Activity';
}

/**
 * Format open count for display
 *
 * @param count - Number of opens
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted count string
 *
 * @example
 * formatOpenCount(5, 'en') // "5 opens"
 * formatOpenCount(5, 'ar') // "٥ مرات فتح"
 */
export function formatOpenCount(count: number, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    const arabicNumber = count.toLocaleString('ar-SA');
    if (count === 0) return 'لا توجد فتحات';
    if (count === 1) return 'مرة واحدة';
    if (count === 2) return 'مرتان';
    return `${arabicNumber} ${count <= 10 ? 'مرات فتح' : 'مرة فتح'}`;
  }

  return count === 1 ? '1 open' : `${count} opens`;
}

/**
 * Get security status description
 *
 * @param score - Security score (0-100)
 * @param language - Display language ('ar' or 'en')
 * @returns Security status description
 *
 * @example
 * getSecurityStatus(95, 'en') // "Excellent"
 * getSecurityStatus(45, 'ar') // "ضعيف"
 */
export function getSecurityStatus(score: number, language: 'ar' | 'en' = 'en'): string {
  if (score >= 90) {
    return language === 'ar' ? 'ممتاز' : 'Excellent';
  }

  if (score >= 70) {
    return language === 'ar' ? 'جيد' : 'Good';
  }

  if (score >= 50) {
    return language === 'ar' ? 'متوسط' : 'Fair';
  }

  return language === 'ar' ? 'ضعيف' : 'Poor';
}
